import React, { useState, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
} from 'react-native';
import { MARKETPLACE_COLORS } from '@/constants/residents/marketplaceColors';
import { useToast } from '@/contexts/admin/ToastContext';
import {
  useAddBankAccountMutation,
  useGenerateCardConnectTokenMutation,
} from '@/store/api';
import { PaymentModalFooter } from '@/components/residents/payments/mobile/makePayment';
import { FloatingLabelInput, PaymentCheckbox } from './shared';

interface ACHFormContentProps {
  onACHAdded: (methodId?: number) => void;
  onBack: () => void;
}

interface FormErrors {
  name?: string;
  routingNumber?: string;
  accountNumber?: string;
  confirmAccountNumber?: string;
}

/**
 * ACH Form Content - Handles bank account input and tokenization
 */
export const ACHFormContent: React.FC<ACHFormContentProps> = ({
  onACHAdded,
  onBack,
}) => {
  const { showSuccess, showError } = useToast();

  // Form state
  const [name, setName] = useState('');
  const [routingNumber, setRoutingNumber] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [confirmAccountNumber, setConfirmAccountNumber] = useState('');
  const [makeDefault, setMakeDefault] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});

  // API mutations
  const [generateToken, { isLoading: tokenizing }] = useGenerateCardConnectTokenMutation();
  const [addBankAccount, { isLoading: addingAccount }] = useAddBankAccountMutation();

  const isLoading = tokenizing || addingAccount;

  const handleNameChange = useCallback((value: string) => {
    setName(value);
    setErrors((prev) => ({ ...prev, name: undefined }));
  }, []);

  const handleRoutingNumberChange = useCallback((value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 9);
    setRoutingNumber(digits);
    setErrors((prev) => ({ ...prev, routingNumber: undefined }));
  }, []);

  const handleAccountNumberChange = useCallback((value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 17);
    setAccountNumber(digits);
    setErrors((prev) => ({ ...prev, accountNumber: undefined }));
  }, []);

  const handleConfirmAccountNumberChange = useCallback((value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 17);
    setConfirmAccountNumber(digits);
    setErrors((prev) => ({ ...prev, confirmAccountNumber: undefined }));
  }, []);

  // Validate form
  const validateForm = useCallback((): boolean => {
    const newErrors: FormErrors = {};

    if (!name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!routingNumber || routingNumber.length !== 9) {
      newErrors.routingNumber = 'Please enter a valid 9-digit routing number';
    }

    if (!accountNumber || accountNumber.length < 4 || accountNumber.length > 17) {
      newErrors.accountNumber = 'Please enter a valid account number';
    }

    if (accountNumber !== confirmAccountNumber) {
      newErrors.confirmAccountNumber = 'Account numbers do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [name, routingNumber, accountNumber, confirmAccountNumber]);

  // Handle save
  const handleSave = useCallback(async () => {
    if (!validateForm()) {
      return;
    }

    try {
      const accountString = `${routingNumber}/${accountNumber}`;

      // Step 1: Tokenize bank account with CardConnect
      const tokenResponse = await generateToken({
        account: accountString,
      }).unwrap();

      if (!tokenResponse.token) {
        throw new Error(tokenResponse.message || 'Failed to tokenize bank account');
      }

      // Step 2: Add bank account to payment methods
      const addResponse = await addBankAccount({
        name: name,
        token: tokenResponse.token,
        isDefault: makeDefault,
        accttype: 'ECHK',
      }).unwrap();

      showSuccess('Bank account added successfully');
      
      const newMethodId = addResponse?.response?.id || addResponse?.id;
      onACHAdded(newMethodId);
    } catch (error: any) {
      const message = error?.data?.message || error?.message || 'Failed to add bank account';
      showError(message);
    }
  }, [
    validateForm,
    routingNumber,
    accountNumber,
    name,
    makeDefault,
    generateToken,
    addBankAccount,
    showSuccess,
    showError,
    onACHAdded,
  ]);

  return (
    <View style={styles.container}>
      <View style={styles.content}>
        {/* Instructions */}
        <Text style={styles.instructionText}>
          Please enter your checking account details.
        </Text>

        {/* Form Fields */}
        <View style={styles.formContainer}>
          <FloatingLabelInput
            label="Name"
            value={name}
            onChangeText={handleNameChange}
            error={errors.name}
            autoCapitalize="words"
            autoCorrect={false}
          />

          <FloatingLabelInput
            label="Routing number"
            value={routingNumber}
            onChangeText={handleRoutingNumberChange}
            error={errors.routingNumber}
            keyboardType="number-pad"
            maxLength={9}
          />

          <FloatingLabelInput
            label="Account number"
            value={accountNumber}
            onChangeText={handleAccountNumberChange}
            error={errors.accountNumber}
            keyboardType="number-pad"
            maxLength={17}
          />

          <FloatingLabelInput
            label="Confirm account number"
            value={confirmAccountNumber}
            onChangeText={handleConfirmAccountNumberChange}
            error={errors.confirmAccountNumber}
            keyboardType="number-pad"
            maxLength={17}
          />
        </View>

        {/* Make Default Checkbox */}
        <PaymentCheckbox
          checked={makeDefault}
          onPress={() => setMakeDefault(!makeDefault)}
          label="Make this my default payment method"
        />
      </View>

      {/* Footer */}
      <View style={styles.footer}>
        <PaymentModalFooter
          onCancel={onBack}
          onProceed={handleSave}
          cancelText="Back"
          proceedText="Save"
          loading={isLoading}
          proceedDisabled={isLoading}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    flex: 1,
    paddingHorizontal: 24,
    paddingVertical: 24,
    gap: 20,
  },
  instructionText: {
    fontSize: 16,
    fontWeight: '400',
    color: MARKETPLACE_COLORS.textPrimary,
    lineHeight: 24,
  },
  formContainer: {
    gap: 8,
  },
  footer: {
    borderTopWidth: 1,
    borderTopColor: MARKETPLACE_COLORS.dividerGray,
  },
});

export default ACHFormContent;
